import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Trash2, Play, Loader2 } from "lucide-react";

export function AudioRecorder({ value, onChange, label, testid }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("ct_token") : "";
  const playUrl = value ? `${backend}${value.url}?auth=${token}` : null;

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await upload(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "note.webm");
      fd.append("kind", "audio");
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
      toast.success("Audio note saved");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (value?.id) { try { await api.delete(`/uploads/${value.id}`); } catch { /* ignore */ } }
    onChange(null);
  };

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      {label && <span className="text-xs text-slate-500 mr-1">{label}</span>}
      {!value ? (
        recording ? (
          <button type="button" onClick={stop} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand text-white text-xs font-semibold animate-pulse" data-testid={`${testid}-stop`}>
            <Square size={14} /> Stop
          </button>
        ) : (
          <button type="button" onClick={start} disabled={uploading} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700" data-testid={`${testid}-record`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />} {uploading ? "Saving…" : "Record"}
          </button>
        )
      ) : (
        <div className="flex items-center gap-2">
          <audio controls src={playUrl} className="h-8" data-testid={`${testid}-audio`} />
          <button type="button" onClick={remove} className="text-brand p-1" data-testid={`${testid}-delete`}><Trash2 size={16} /></button>
        </div>
      )}
    </div>
  );
}
